import { createContext, useContext, useState } from "react";

export interface catMed {
    label: string;
    value: string;
}

export interface SearchFieldsDefinition {
    keyword: string;
    setKeyword: (keyword) => void;
    location: string;
    setLocation: (location) => void;
    startDate: string;
    setStartDate: (startDate) => void;
    endDate: string;
    setEndDate: (endDate) => void;
    categories: Array<catMed>;
    setCategories: (categories) => void;
    media: Array<catMed>;
    setMedia: (media) => void;
    filter: (events) => void;
    showModal: boolean;
    setShowModal: (showModal) => void;
}

const defaults: SearchFieldsDefinition = {
    keyword: "", 
    setKeyword: () => {return},
    location: "", 
    setLocation: () => {return},
    startDate: "", 
    setStartDate: () => {return},
    endDate: "",
    setEndDate: () => {return},
    categories: [], 
    setCategories: () => {return},
    media: [], 
    setMedia: () => {return},
    filter: () => {return}, 
    showModal: false,
    setShowModal: () => {return}
}

export const SearchFieldsContext = createContext<SearchFieldsDefinition>(defaults);

export default function SearchFieldsProvider({ children }) {
    const [keyword, setKeyword] = useState(defaults.keyword);
    const [location, setLocation] = useState(defaults.location);
    const [startDate, setStartDate] = useState(defaults.startDate);
    const [endDate, setEndDate] = useState(defaults.endDate);
    const [categories, setCategories] = useState(defaults.categories)
    const [media, setMedia] = useState(defaults.media)
    const [showModal, setShowModal] = useState(defaults.showModal);

    function haveCommonItems(arr1, arr2) {
        const set1 = new Set(arr1);
        const commonItems = arr2.filter(item => set1.has(item));
        return commonItems.length > 0;
    }

    function returnCommonItems(event, eventCategories, inputCategories) {
        // return event if eventCategories and inputCategories have common items 
        if (haveCommonItems(eventCategories, inputCategories)) {
            return event;
        }
        return;
    }

    const isCategoryOrMediumPresent = (event) => {
        // takes an event and returns true if it has any of the categories or media selected by the user 
        // if both filters are being used, check that all that match category also match any of the media 
        const catInput = [];
        const medInput = [];
        categories.forEach((element) => catInput.push(element.value));
        media.forEach((element) => medInput.push(element.value));

        const eventCategories = [];
        const eventMedia = [];

        // if just categories
        if (medInput.length === 0) {
            event.category.forEach((element) => eventCategories.push(element.name));
            const catMatch = haveCommonItems(catInput, eventCategories);
            return catMatch;
        }
        // if just media 
        if (catInput.length === 0) {
            event.medium.forEach((element) => eventMedia.push(element.name));
            const medMatch = haveCommonItems(medInput, eventMedia);
            return medMatch;
        }
        // if both 
        if (catInput.length > 0 && medInput.length > 0) {
            event.category.forEach((element) => eventCategories.push(element.name));
            // if the event has any of the category inputs, return the whole event. 
            const eventIfHasCategory = returnCommonItems(event, eventCategories, catInput);
            
            // check if it has any of the media inputs. 
            if (eventIfHasCategory) {
                event.medium.forEach((element) => eventMedia.push(element.name));
                return haveCommonItems(eventMedia, medInput)
            };      
        }        
    };

    const filter = (events) => {
        let eventsResultFirst = [];
        let eventsResultInter = [];
        let eventsResult = [];
        let eventsResultFiltered = [];
    
        if (startDate && endDate) {
            eventsResultFirst = events.filter((event) => event.start_time >= startDate && event.end_time <= endDate);
        } else if (startDate && !endDate) {
            eventsResultFirst = events.filter((event) => event.start_time > startDate);
        } else if (!startDate && endDate) {
            eventsResultFirst = events.filter((event) => event.end_time <= endDate);
        } else {
            eventsResultFirst = events;
        };
        
        if (location.length > 0) { 
            // check all venues 
            eventsResultInter = eventsResultFirst.filter(event => `${event.venues[0].address_city.toLowerCase()} ${event.venues[0].name.toLowerCase()} ${event.venues[0].neighborhood.toLowerCase()}`.includes(location));
        } else {
            eventsResultInter = eventsResultFirst;
        };
    
        if (keyword.length > 0) {
            eventsResultInter.forEach(event => {
                const categoriesMediaArray = [];
                event.category.map((cat) => categoriesMediaArray.push(cat.name));
                event.medium.map((med) => categoriesMediaArray.push(med.name));
                const categoriesMediaString = categoriesMediaArray.join(' ');
                let free = (event.ticket_price_max === 0 ? "free" : "")
    
                const stringToCheck = `${event.name.toLowerCase()} ${event.description.toLowerCase()} ${categoriesMediaString.toLowerCase()} 
                ${free} ${event.venues[0].accessibility_info.toLowerCase()} ${event.venues[0].address_city.toLowerCase()} ${event.venues[0].name.toLowerCase()} ${event.venues[0].neighborhood.toLowerCase()}`;
                if (stringToCheck.includes(keyword.toLowerCase())) {
                    eventsResult.push(event);
                }
            })
        } else {
            eventsResult = eventsResultInter;
        }

        if (categories.length > 0 || media.length > 0) {
            eventsResultFiltered = eventsResult.filter((event) => isCategoryOrMediumPresent(event) === true);
        } else {
            eventsResultFiltered = eventsResult;
        }

        return(eventsResultFiltered);
    };
    
    return (
        <SearchFieldsContext.Provider value={{ keyword, setKeyword, location, setLocation, startDate, setStartDate, 
        endDate, setEndDate, filter, showModal, setShowModal, categories, setCategories, media, setMedia}}>
            {children}
        </SearchFieldsContext.Provider>
    );
}

export function useSearchFields() {
    const {keyword, setKeyword, location, setLocation, startDate, setStartDate, endDate, categories, setCategories, media, setMedia,
        setEndDate, filter, showModal, setShowModal} = useContext(SearchFieldsContext);

    return {
        keyword, 
        setKeyword,
        location, 
        setLocation,
        startDate,
        setStartDate,
        endDate,
        setEndDate, 
        categories, 
        setCategories, 
        media, 
        setMedia, 
        filter, 
        showModal, 
        setShowModal
    };
}